import {
  TaskKanbanReadable,
  tasksKanbanListInfiniteOptions,
  tasksKanbanListInfiniteQueryKey,
  tasksPartialUpdateMutation,
  taskStatusesListInfiniteOptions,
  taskStatusesListInfiniteQueryKey
} from '@crm/api'
import { TaskForm } from '@crm/components'
import { TaskFilter, TasksKanbanCard } from '@crm/features/tasks/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { getActivePath } from '@crm/utils'
import { Kanban, SearchInput, TitleSection } from '@shared/components'

import { useQueryUpdate } from '@shared/hooks'

import { Flex, Modal, Segmented } from 'antd'
import { Kanban as KanbanIcon, TextalignJustifycenter } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import type { SegmentedProps } from 'antd'

const formName = 'kanbanModalTask'

const MODAL_TYPES = {
  edit: 'edit'
} as const

type ModalType = keyof typeof MODAL_TYPES | null

export const TasksKanban = () => {
  const [selectedTaskId, setSelectedTaskId] = useState<number | undefined>()
  const [modalType, setModalType] = useState<ModalType>(null)
  const { params } = useQueryUpdate()

  const navigate = useAppNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const kanbanQueryParams = {
    search: params?.search,
    owner: params?.owner,
    created_by: params?.created_by,
    deadline_start: params?.deadline_start,
    lead: params?.lead,
    type: params?.type,
    priority: params?.priority
  }

  const onCloseModal = useCallback(() => {
    setModalType(null)
    setSelectedTaskId(undefined)
  }, [])

  const onEdit = useCallback((task: TaskKanbanReadable) => {
    setSelectedTaskId(task.id)
    setModalType(MODAL_TYPES.edit)
  }, [])

  const segmentedOptions = useMemo<SegmentedProps<string>['options']>(
    () => [
      { value: paths.tasks.root, label: <TextalignJustifycenter size={16} /> },
      { value: paths.tasks.kanban, label: <KanbanIcon size={16} /> }
    ],
    []
  )

  return (
    <>
      <TitleSection title={t('menu.tasks')} />

      <Kanban
        header={
          <Flex justify="space-between" align="start" gap={8}>
            <Flex gap={8} wrap align="start">
              <Flex>
                <SearchInput />
              </Flex>

              <TaskFilter />
            </Flex>

            <Segmented
              value={getActivePath(pathname)}
              onChange={(value) => navigate(value)}
              options={segmentedOptions}
            />
          </Flex>
        }
        cardRender={(task: TaskKanbanReadable) => (
          <TasksKanbanCard data={task} onEdit={() => onEdit(task)} />
        )}
        statusesQuery={taskStatusesListInfiniteOptions}
        pipelineListQuery={tasksKanbanListInfiniteOptions}
        pipelineListUpdateMutation={tasksPartialUpdateMutation}
        statusesQueryKey={taskStatusesListInfiniteQueryKey}
        pipelineListQueryKey={tasksKanbanListInfiniteQueryKey}
        pipelineListQueryOptions={kanbanQueryParams}
      >
        <Modal
          width={800}
          open={modalType !== null}
          maskClosable={false}
          title={t('task.task')}
          onCancel={onCloseModal}
          destroyOnHidden
          cancelText={t('buttons.cancel')}
          okText={t('buttons.save')}
          okButtonProps={{
            htmlType: 'submit',
            form: formName
          }}
        >
          {selectedTaskId && (
            <TaskForm
              formName={formName}
              id={selectedTaskId}
              onCancel={onCloseModal}
            />
          )}
        </Modal>
      </Kanban>
    </>
  )
}
